// React Hooks
import { useState, useEffect } from "react";

// Third-Party Libraries
import axios from "axios";

// MUI Components
import {
  Modal,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Typography,
  Stack,
  Paper,
  Chip,
  IconButton,
  Dialog,
} from "@mui/material";

// MUI Icons
import {
  EditOutlined as EditOutlinedIcon,
  DeleteOutline as DeleteOutlineIcon,
  ApartmentOutlined as ApartmentOutlinedIcon,
  LabelOutlined as LabelOutlinedIcon,
  CalendarTodayOutlined as CalendarTodayOutlinedIcon,
  NumbersOutlined as NumbersOutlinedIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

import useAxiosInstance from "../helpers/useAxiosInstance";



const bedroomOptions = [
  "All Units",
  "Studio",
  "1 Bed",
  "2 Bed",
  "3 Bed",
  "4 Bed",
  "Vacant",
  "NTV",
  "All Others",
];

const initialState = {
  id: null,
  unitNumbers: [],
  floorPlans: [],
  leaseTerm: "",
  unitTypes: [],
  monthsFree: "",
  concession: "",
  concessionTb: "",
  dateFrom: "",
  dateTo: "",
  notes: "",
};

const SpecialCalculator = ({
  calculator,
  setCalculator,
  messageApi,
  contextHolder,
}) => {
  const [formData, setFormData] = useState({
    id: null,
    unitNumbers: [],
    floorPlans: [],
    leaseTerm: "",
    unitTypes: [],
    monthsFree: "",
    concession: "",
    concessionTb: "",
    dateFrom: "",
    dateTo: "",
    notes: "",
  });
  const axiosInstance = useAxiosInstance()

  const [allASpecials, setAllAspecials] = useState([]);

  const [unitOptions, setUnitOptions] = useState([]);
  const [floorPlanOptions, setFloorPlanOptions] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    if (calculator?.data?.id) {
      getAllUnitDetaiils(calculator?.data?.id);
      getAllSpecialsByPropertyId(calculator?.data?.id);
    }
  }, [calculator?.data?.id]);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event?.target?.value }));
  };

  const onClose = () => {
    setCalculator((prev) => ({ data: null, ...prev }));
  };

  const handleClose = () => {
    setCalculator((prev) => ({ data: null, open: false }));
    setFormData(initialState);
  };

  const errorMsg = () => {
    messageApi.open({
      type: "error",
      content: "Operation Failed!",
    });
  };

  const handleEditFilter = (special) => {
    setFormData({
      id: special?.id,
      unitNumbers: special?.unit_numbers || [],
      floorPlans: special?.floor_plans || [],
      leaseTerm: special?.lease_term || "",
      unitTypes: special?.unit_types || [],
      monthsFree: special?.months_free || "",
      concession: special?.custom_concession || "",
      concessionTb: special?.custom_concession || "",
      dateFrom: special?.date_from?.split("T")[0] || "",
      dateTo: special?.date_to?.split("T")[0] || "",
      notes: special?.notes || "",
    });
  };

  const handleSave = async () => {
    const payload = {
      propertyId: calculator?.data?.id,
      ...formData,
      custom_concession: formData?.concessionTb,
    };

    try {
      setSaveLoading(true);

      const url = formData.id
        ? `${process.env.REACT_APP_API_URL}/specials/update/${formData.id}`
        : `${process.env.REACT_APP_API_URL}/specials`;

      const method = formData.id ? "put" : "post";

      const response = await axiosInstance[method](url, payload);

      if (response.data.success) {
        messageApi.open({
          type: "success",
          content: formData.id
            ? "Special Updated Successfully!"
            : "Special Filter Added Successfully!",
        });
        if (!formData?.id) {
          handleClose();
        }

        getAllSpecialsByPropertyId(calculator?.data?.id);
        setFormData(initialState);
      } else {
        errorMsg();
      }
    } catch (error) {
      errorMsg();
      console.error("Error saving special:", error);
    } finally {
      setSaveLoading(false);
    }
  };

  const getAllSpecialsByPropertyId = async (propertyId) => {
    try {
      await axiosInstance
        .get(`${process.env.REACT_APP_API_URL}/specials/${propertyId}`)
        .then((res) => {
          const specialsData = res?.data?.data || [];
          setAllAspecials(specialsData);
        })
        .catch((error) => {
          setAllAspecials([]);
          console.error("Error fetching specials:", error);
        });
    } catch (error) {
      console.error("Unexpected error:", error);
      setAllAspecials([]);
    }
  };

  const handleDeleteFilter = async (filterId) => {
    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_URL}/specials/delete/${filterId}`
      );
      const updated = allASpecials.filter((f) => f?.id !== filterId);
      setAllAspecials(updated);
    } catch (err) {
      console.error("Failed to delete filter:", err);
    }
  };

  const getAllUnitDetaiils = async (propertyId) => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_URL}/get-units-by-property-id/${propertyId}`
      );
      const { data = null } = response?.data || {};

      if (data && data?.length) {
        const distinctModelNames = [
          ...new Set(data.map((unit) => unit?.model_name)),
        ].sort();
        const distinctUnitNums = [
          ...new Set(data.map((unit) => unit?.unit)),
        ].sort();
        setUnitOptions(distinctUnitNums);
        setFloorPlanOptions(distinctModelNames);
      } else {
        setUnitOptions([]);
        setFloorPlanOptions([]);
      }
    } catch (error) {
      setUnitOptions([]);
      setFloorPlanOptions([]);
      console.error("Error occured at:getUnitDetaiils", error);
    }
  };

  return (
    <Dialog
      open={calculator?.open}
      onClose={onClose}
      maxWidth="md"
      // fullWidth
      PaperProps={{
        className: "rounded-xl",
        sx: { height: "80vh", overflow: "hidden" },
      }}
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex justify-between items-center px-6 pt-4 pb-2 border-b">
          <Typography variant="h6" fontWeight={400}>
            {formData?.id ? "Update" : "Add"} Special
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </div>

        {/* Body */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Section - Form */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
            {/* Unit Types */}
            <FormControl fullWidth size="small">
              <InputLabel id="unit-type-label" sx={{ fontSize: 13 }}>
                Unit Type
              </InputLabel>
              <Select
                labelId="unit-type-label"
                multiple
                value={formData.unitTypes}
                onChange={handleChange("unitTypes")}
                input={<OutlinedInput label="Unit Type" />}
                renderValue={(selected) => selected.join(", ")}
                sx={{ fontSize: 13 }}
              >
                {bedroomOptions.map((option, idx) => (
                  <MenuItem key={idx} value={option}>
                    <Checkbox checked={formData?.unitTypes?.includes(option)} />
                    <ListItemText primary={option} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Floor Plans */}
            <FormControl fullWidth size="small">
              <InputLabel id="floor-plans-label" sx={{ fontSize: 13 }}>
                Floor Plans
              </InputLabel>
              <Select
                labelId="floor-plans-label"
                multiple
                value={formData.floorPlans}
                onChange={handleChange("floorPlans")}
                input={<OutlinedInput label="Floor Plans" />}
                renderValue={(selected) => selected.join(", ")}
                sx={{ fontSize: 13 }}
              >
                {floorPlanOptions.map((plan, idx) => (
                  <MenuItem key={idx} value={plan}>
                    <Checkbox checked={formData?.floorPlans?.includes(plan)} />
                    <ListItemText primary={plan} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Unit Numbers */}
            <FormControl fullWidth size="small">
              <InputLabel id="unit-numbers-label" sx={{ fontSize: 13 }}>
                Unit Numbers
              </InputLabel>
              <Select
                labelId="unit-numbers-label"
                multiple
                value={formData?.unitNumbers}
                onChange={handleChange("unitNumbers")}
                input={<OutlinedInput label="Unit Numbers" />}
                renderValue={(selected) => selected.join(", ")}
                sx={{ fontSize: 13 }}
              >
                {unitOptions.map((unit, idx) => (
                  <MenuItem key={idx} value={unit}>
                    <Checkbox checked={formData?.unitNumbers?.includes(unit)} />
                    <ListItemText primary={unit} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Lease Term */}
            <TextField
              fullWidth
              size="small"
              label="Lease Term (e.g. 6, 12 months)"
              value={formData?.leaseTerm}
              onChange={handleChange("leaseTerm")}
            />

            {/* Months Free */}
            <TextField
              fullWidth
              size="small"
              label="Months Free"
              type="number"
              value={formData?.monthsFree}
              onChange={handleChange("monthsFree")}
            />

            {/* Concession */}
            <TextField
              fullWidth
              size="small"
              label="$ Concession"
              value={formData?.concessionTb}
              onChange={handleChange("concessionTb")}
            />

            {/* Notes */}
            <TextField
              fullWidth
              size="small"
              label="Notes"
              value={formData?.notes}
              onChange={handleChange("notes")}
            />

            {/* Date Range */}
            <div className="flex gap-3">
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={formData?.dateFrom}
                onChange={handleChange("dateFrom")}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={formData?.dateTo}
                onChange={handleChange("dateTo")}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </div>
          </div>

          {/* Right Section - Specials List */}
          <div className="w-[400px] border-l px-4 py-4">
            <Typography variant="subtitle1" mb={1}>
              Existing Specials
            </Typography>
            {allASpecials.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No specials saved for this property.
              </Typography>
            ) : (
              <Box sx={{ height: "90%", overflow: "scroll", pr: 1 }}>
                <Stack spacing={1}>
                  {allASpecials.map((filter, i) => (
                    <Paper
                      key={filter._id || i}
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: "#fafafa",
                        position: "relative",
                      }}
                    >
                      <div className="flex items-center absolute top-0 right-0">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditFilter(filter)}
                          // sx={{ position: "absolute", top: 8, right: 40 }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>

                        {/* Delete Button */}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteFilter(filter?.id)}
                          // sx={{ position: "absolute", top: 8, right: 8 }}
                        >
                          <DeleteOutlineIcon color="error" fontSize="small" />
                        </IconButton>
                      </div>
                      {/* Edit Button */}

                      <Stack spacing={1}>
                        {filter?.floor_plans?.length > 0 && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <ApartmentOutlinedIcon
                              fontSize="small"
                              color="action"
                            />
                            <Typography variant="body2">
                              <strong>Floor Plans:</strong>{" "}
                              {filter.floor_plans.join(", ")}
                            </Typography>
                          </Box>
                        )}

                        {filter?.unit_numbers?.length > 0 && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <NumbersOutlinedIcon
                              fontSize="small"
                              color="action"
                            />
                            <Typography variant="body2">
                              <strong>Unit Numbers:</strong>{" "}
                              {filter.unit_numbers.join(", ")}
                            </Typography>
                          </Box>
                        )}

                        {filter?.unit_types?.length > 0 && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <LabelOutlinedIcon
                              fontSize="small"
                              color="action"
                            />
                            <Typography variant="body2">
                              <strong>Unit Types:</strong>{" "}
                              {filter.unit_types.join(", ")}
                            </Typography>
                          </Box>
                        )}

                        {filter?.lease_term && (
                          <Box display="flex" alignItems="center" gap={1}>
                            <CalendarTodayOutlinedIcon
                              fontSize="small"
                              color="action"
                            />
                            <Typography variant="body2">
                              <strong>Lease Term:</strong> {filter.lease_term}{" "}
                              mo
                            </Typography>
                          </Box>
                        )}

                        <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                          {filter?.months_free && (
                            <Chip
                              label={`+${filter.months_free} mo free`}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                          {filter?.custom_concession && (
                            <Chip
                              label={`$${filter.custom_concession} concession`}
                              size="small"
                              color="info"
                              variant="outlined"
                            />
                          )}
                          {filter?.notes && (
                            <Chip
                              label={`Note : ${filter.notes}`}
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end items-center gap-3 px-6 py-3 border-t">
          <Button
            disabled={saveLoading}
            onClick={handleClose}
            size="small"
            variant="outlined"
          >
            Cancel
          </Button>
          {formData?.id && (
            <Button
              disabled={saveLoading}
              onClick={() => setFormData(initialState)}
              size="small"
              variant="outlined"
            >
              Reset
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            disabled={
              saveLoading ||
              (!formData?.unitTypes?.length &&
                !formData?.floorPlans?.length &&
                !formData?.unitNumbers?.length)
            }
            onClick={handleSave}
          >
            {saveLoading ? "Saving..." : formData?.id ? "Update" : "Add"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default SpecialCalculator;
